/** @jsxImportSource @emotion/react */
import type { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { CameraIconSolid, PauseGlyph, PlayGlyph } from '@/Client/Icons';

import { formatPlaybackTimeSeconds } from '../scanVideoFormatLib';
import {
  collectVideoGroupSegmentRanges,
  collectVideoGroupSegments,
  getActiveVideoGroupSegment,
  type VideoGroupSegment,
} from './videoGroupTimelineLib';
import { DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES, type VideoPlaybackPanelStyles } from './videoPlaybackPanelStyles';
import { VideoPlaybackTimeline, type VideoPlaybackTimelineDensity } from './VideoPlaybackTimeline';

export type VideoPlaybackPanelProps = {
  playbackUrl: string;
  density?: VideoPlaybackTimelineDensity;
  scanRoot?: ScanRootOptimized;
  scanVideoId?: string;
  /** Scope group for the editor — brightens that room's band on the scrubber. */
  highlightGroupId?: string;
  autoPlay?: boolean;
  /** Seek once metadata is ready (e.g. scope/group/task videoOffset.startSeconds). */
  initialSeekStartSeconds?: number;
  videoTestId?: string;
  styles?: VideoPlaybackPanelStyles;
  /** When set, the panel's `<video>` is attached here for narration sync in host panels. */
  videoRef?: RefObject<HTMLVideoElement | null>;
  showCaptureButton?: boolean;
  captureDisabled?: boolean;
  onCaptureFrame?: () => void | Promise<void>;
};

/** Custom walkthrough player with a group-aware timeline (density selects track detail). */
export const VideoPlaybackPanel = ({
  playbackUrl,
  density = 'segment',
  scanRoot,
  scanVideoId,
  highlightGroupId,
  autoPlay = false,
  initialSeekStartSeconds,
  videoTestId = 'video-playback-panel',
  styles = DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES,
  videoRef: externalVideoRef,
  showCaptureButton = false,
  captureDisabled = false,
  onCaptureFrame,
}: VideoPlaybackPanelProps) => {
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const videoRef = externalVideoRef ?? internalVideoRef;
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const timelineSegments = useMemo(
    () => collectVideoGroupSegments(scanRoot, scanVideoId, videoDuration),
    [scanRoot, scanVideoId, videoDuration],
  );

  const segmentRanges = useMemo(
    () => collectVideoGroupSegmentRanges(scanRoot, scanVideoId, videoDuration),
    [scanRoot, scanVideoId, videoDuration],
  );

  const activeSegment = useMemo(
    () =>
      getActiveVideoGroupSegment(
        segmentRanges.length ? segmentRanges : timelineSegments,
        currentTime,
      ),
    [segmentRanges, timelineSegments, currentTime],
  );

  useEffect(() => {
    if (!isPlaying) return;
    let frameId = 0;
    const track = () => {
      if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
      frameId = requestAnimationFrame(track);
    };
    frameId = requestAnimationFrame(track);
    return () => cancelAnimationFrame(frameId);
  }, [isPlaying, videoRef]);

  useEffect(() => {
    if (!autoPlay || !videoRef.current) return;
    void videoRef.current.play();
  }, [autoPlay, playbackUrl, videoRef]);

  useEffect(() => {
    if (initialSeekStartSeconds == null) return;
    const video = videoRef.current;
    if (!video) return;

    const seekToInitialTime = () => {
      video.currentTime = initialSeekStartSeconds;
      setCurrentTime(initialSeekStartSeconds);
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekToInitialTime();
      return;
    }

    video.addEventListener('loadedmetadata', seekToInitialTime, { once: true });
    return () => video.removeEventListener('loadedmetadata', seekToInitialTime);
  }, [initialSeekStartSeconds, playbackUrl, videoRef]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play();
    else video.pause();
  };

  const seekTo = (videoTimeSeconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = videoTimeSeconds;
    setCurrentTime(videoTimeSeconds);
  };

  const seekToSegmentAnchor = (segment: VideoGroupSegment) => {
    seekTo(segment.anchorSeconds);
  };

  return (
    <div css={styles.frame} data-testid={'video-playback-panel'}>
      <video
        ref={videoRef}
        css={styles.video}
        src={playbackUrl}
        crossOrigin={'anonymous'}
        playsInline
        preload={'auto'}
        data-testid={videoTestId}
        onClick={togglePlayback}
        onLoadedMetadata={event => setVideoDuration(event.currentTarget.duration)}
        onTimeUpdate={event => {
          if (!isPlaying) setCurrentTime(event.currentTarget.currentTime);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />

      <div css={styles.overlayBar}>
        <VideoPlaybackTimeline
          density={density}
          durationSeconds={videoDuration}
          currentTimeSeconds={currentTime}
          segments={timelineSegments}
          activeSegment={activeSegment}
          highlightGroupId={highlightGroupId}
          onSeek={seekTo}
          onSeekToSegmentAnchor={seekToSegmentAnchor}
          styles={styles}
        />

        <div css={styles.controls} data-testid={'video-playback-controls'}>
          <button
            type={'button'}
            css={styles.controlButton}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={togglePlayback}
          >
            {isPlaying ? <PauseGlyph size={17} color={'currentColor'} /> : <PlayGlyph size={17} color={'currentColor'} />}
          </button>
          <span css={styles.controlTime}>
            {formatPlaybackTimeSeconds(currentTime)} / {formatPlaybackTimeSeconds(videoDuration)}
          </span>
          {showCaptureButton && (
            <button
              type={'button'}
              css={[styles.controlButton, styles.captureButton]}
              aria-label={'Capture frame as photo'}
              disabled={captureDisabled}
              data-testid={'video-playback-capture-frame'}
              onClick={() => void onCaptureFrame?.()}
            >
              <CameraIconSolid size={17} color={'currentColor'} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
